import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { EditOutline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { CounterPill, LinkButton, Stack, Typography } from '@lyearn/ui';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import GroupTab from '@/modules/corporate/modules/group/components/GroupTab';
import ShowMoreList from '@/modules/learner/components/ShowMoreList';
import { learnerWidgetStyles } from '@/modules/learner/views/LearnerProfile/styles';
import { UserCollectionGroupType, WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import LearnerEditGroupsModal from '../LearnerEditGroupsModal';
import { LearnerGroupsWidgetProps } from './types';

function LearnerGroupsWidget({ learnerId, userGroups, onAction }: LearnerGroupsWidgetProps) {
  const { t } = useTranslation();
  const groups = userGroups || [];
  const [open, { on, off }] = useToggle(false);

  const { isAccessible: isWorkspaceAccessible, showPermissionSnackbar } =
    useWorkspaceAccessControl();

  const onEdit = useCallback(() => {
    isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
      ? on()
      : showPermissionSnackbar('edit', 'learner');
  }, [isWorkspaceAccessible, on, showPermissionSnackbar]);

  return (
    <Stack className={cn(learnerWidgetStyles, 'group')} direction="col">
      <Stack alignItems="center">
        <Typography variant="heading-xs">{t('LearnerProfile:Groups.Groups')}</Typography>
        {groups.length ? (
          <>
            <CounterPill className="ms-8" label={groups.length} />
            <LinkButton
              className="py-2 px-4 opacity-0 ms-auto group-hover:opacity-100"
              color={'internal'}
              startIcon={<EditOutline />}
              onClick={onEdit}>
              {t('Common:Actions.Edit')}
            </LinkButton>
          </>
        ) : null}
      </Stack>
      {groups.length ? (
        <Stack className="mt-24" direction="col" gapY={16}>
          <ShowMoreList
            defaultShowCount={3}
            items={groups}
            renderItem={(group) => (
              <GroupTab
                key={group.id}
                group={group as UserCollectionGroupType}
                mode="normal-small"
              />
            )}
          />
        </Stack>
      ) : (
        <AddBlock
          className="mt-20"
          length="short"
          title={t('LearnerProfile:Groups.AddGroups')}
          onClick={onEdit}
        />
      )}
      {open ? (
        <LearnerEditGroupsModal
          learnerId={learnerId}
          userGroups={groups}
          onAction={onAction}
          onClose={off}
        />
      ) : null}
    </Stack>
  );
}

export default LearnerGroupsWidget;
